import {useCallback, useEffect, useMemo, useState} from "react";
import Box from "@mui/material/Box";
import {Chart} from "react-google-charts";
import Grid from "@mui/material/Grid";
import {Paper, Typography} from "@mui/material";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import {googleChartTooltipBubble, ToggleButton} from "../../../utils/uiUtils";
import {variantDataRetrieverMapDonutGraph} from "./donutGraphDataRetrieving";
import {useDashboardJobScope} from "../../../views/JobContextProvider/useDashboardJobScope";
import {variantRawDataProcessorMapDonutGraph} from "./donutGraphDataProcessing";
import {getLargePaletteShadesOrdered} from "./donutGraphUtils";
import {DonutChartSkeleton} from "../graphSkeleton";
import {formatData} from "../../../utils/utils";


const DonutCenter = ({total, metric}) => {
    const [valueString, setValueString] = useState('');

    useEffect(() => {
        const ui_class = metric === 'Size' ? 'data_size_iso_units' : 'files_count';
        const newStringValue = formatData(total, ui_class);
        setValueString(newStringValue);
    }, [total, metric]);

    return (
        <Box
            sx={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                pointerEvents: "none",            // let clicks go through to the chart
                textAlign: "center",
                color: "white",
                fontFamily: "Poppins",
                fontSize: 14,
                lineHeight: 1.2,
            }}
        >
            <strong>Total</strong>
            <br/>
            {valueString}
        </Box>
    )
};

/**
 * DonutGraph component renders a Donut graph based on the provided variant.
 * Depending on the variant, it retrieves the data from different sources and processes it accordingly.
 * Once data is processed, it displays the graph using the Chart component from react-google-charts.
 * @param {string | null} variant - The variant to determine which data to retrieve and how to process it. In case of single variant, it can be null.
 * @param {string | null} variantMetric - The metric to be used for the Donut graph. If not provided, defaults to null and internal switch will be enabled.
 * @param {string} title - The title of the graph, used for accessibility purposes.
 * @constructor
 */
export const DonutGraph = ({variant, variantMetric = null, title = '', rawData: externalRawData, loading: externalLoading}) => {
    const acceptedVariants = [undefined, 'defValue', 'backup', 'storagePolicies'];
    useEffect(() => {
        if (variant && !acceptedVariants.includes(variant)) throw new Error(`Unexpected variant: ${variant}`);
    }, [variant]);

    const [internalRawData, setInternalRawData] = useState([]);
    const [internalLoading, setInternalLoading] = useState(false);
    // Same scope rule as the parent that usually feeds this component (DoubleDonutGraph): a standalone donut
    // reports on the same `xag_*` aggregates, so it has to read 'All Jobs' the same way or the two would
    // disagree wherever this one is mounted on its own. See useDashboardJobScope.
    const {jobIds, cid, jobIdsKey, waiting, nothingOnboarded} = useDashboardJobScope();

    const rawData = externalRawData !== undefined ? externalRawData : internalRawData;
    const loading = externalLoading !== undefined ? externalLoading : internalLoading;

    const [metric, setMetric] = useState(variantMetric ?? "Size");

    const baseGraphOptions = {
        backgroundColor: '#3a3e3f',
        legend: {
            textStyle: {
                color: 'white',
                fontName: 'Poppins',
                fontSize: 12,
                bold: false,
                italic: false
            },
            position: 'bottom',
            alignment: 'center',
        },
        pieHole: 0.5,
        colors: getLargePaletteShadesOrdered(0),
        pieSliceTextStyle: {
            color: 'black',
            fontName: 'Poppins',
        },
        pieSliceBorderColor: '#3a3e3f',
        chartArea: {left: 0, top: 30, width: '100%', height: '80%'},
        fontName: 'Poppins',
        tooltip: {
            showColorCode: true,
            trigger: 'focus',
            isHtml: true,
            ignoreBounds: true
        },
        ...title ? {
            title: title,
            titleTextStyle: {
                color: 'white',
                fontName: 'Poppins',
            }
        } : {},
    };

    const [footNoteInstructions, setFootNoteInstructions] = useState(null);
    // const [footNoteInstructions, setFootNoteInstructions] = useState("* Zoom in - click | Zoom out - right click");

    /**
     * --------------------------------------------------------
     * Data retrieval
     * --------------------------------------------------------
     */

    /**
     * Every time the variant changes, we retrieve the data for that variant and update the rawData state.
     * Skipped when rawData is provided externally (e.g. from DoubleDonutGraph parent).
     */
    useEffect(() => {
        if (externalRawData !== undefined) return;
        // Waiting on the header's job list, so the scope is not known yet; nothing onboarded, so there is
        // nothing to ask for. Neither is a reason to fall back to an unscoped query — see the parent.
        if (waiting) {
            setInternalLoading(true);
            return;
        }
        if (nothingOnboarded) {
            setInternalRawData([]);
            setInternalLoading(false);
            return;
        }
        let isMounted = true;
        const controller = new AbortController();
        const onAbort = () => {
            if (isMounted) setInternalLoading(false);
        };
        controller.signal.addEventListener('abort', onAbort, {once: true});

        const dataRetrieverFn = variantDataRetrieverMapDonutGraph[variant] ?? variantDataRetrieverMapDonutGraph.defValue;
        // `background` for the same reason as in DoubleDonutGraph: this graph shows its own spinner, so the
        // full-window wash would only block the portal on top of it. Marked here too, even though the
        // dashboard feeds this component externally and so never reaches this line, because a standalone
        // DonutGraph mounted anywhere else would otherwise still hold the whole window up.
        dataRetrieverFn(setInternalRawData, setInternalLoading, jobIds, cid, {signal: controller.signal, background: true});

        return () => {
            isMounted = false;
            controller.abort();
            controller.signal.removeEventListener('abort', onAbort);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [variant, externalRawData, jobIdsKey, cid, waiting, nothingOnboarded]);

    /**
     * --------------------------------------------------------
     * Data processing
     * --------------------------------------------------------
     */

    const {processedData, processedDataTotal} = useMemo(() => {
        const rawDataProcessingFn = variantRawDataProcessorMapDonutGraph[variant] ?? variantRawDataProcessorMapDonutGraph.defValue;
        return rawDataProcessingFn(rawData, metric);
    }, [rawData, variant, metric]);


    /**
     * --------------------------------------------------------
     * Tooltip rendering
     * Tooltip can be a function provided to the graph options for rendering custom tooltips (like in TreeMapGraph) or static HTML injected in every data point (like in DonutGraph).
     * If the second, the injection is done within the processData enrichment function.
     * --------------------------------------------------------
     */

    const isTooltipDataInjected = true;

    const renderTooltip = useCallback((dataPoint, index) => {
        const categoryName = dataPoint[0] ?? 'Unknown Category';
        const size = dataPoint[1] ?? 0;
        const ui_class = metric === 'Size' ? 'data_size_iso_units' : 'files_count';
        const metricLabel = metric === 'Size' ? 'Size' : 'Count';

        return googleChartTooltipBubble(
            `<div style="text-align: left;">
                    <strong>Category:</strong> ${categoryName}
                    <br/>
                    <strong>${metricLabel}:</strong> ${formatData(size, ui_class)} (${formatData(size / processedDataTotal, "percentage")})
                    </div>`,
        );
    }, [metric, variant, processedDataTotal]);

    const enrichedProcessedData = useMemo(() => {
        const header = isTooltipDataInjected ?
            ["Category Name", "Value", {role: "tooltip", type: "string", p: {html: true}}] :
            ["Category Name", "Value"];

        const localEnrichedData = processedData.map((dataPoint, index) => {
            return isTooltipDataInjected ?
                [dataPoint[0], dataPoint[1], renderTooltip(dataPoint, index)] :
                [dataPoint[0], dataPoint[1]];
        });
        return [header, ...localEnrichedData];
    }, [processedData, variant, metric, renderTooltip]);

    /**
     * --------------------------------------------------------
     * Graph options
     * --------------------------------------------------------
     */

    const colorsPalette = {
        defValue: getLargePaletteShadesOrdered(0),
        backup: getLargePaletteShadesOrdered(0),
        storagePolicies: getLargePaletteShadesOrdered(1),
    };

    const graphOptions = useMemo(() => {
        return {
            ...baseGraphOptions,
            ...(title ? {title, titleTextStyle: {color: "white", fontName: "Poppins"}} : {}),
            colors: colorsPalette[variant] ?? colorsPalette.defValue,
            generateTooltip: isTooltipDataInjected ? null : renderTooltip,
            pieSliceText: "none"
        };
    }, [variant, title, renderTooltip]);

    /**
     * --------------------------------------------------------
     * Rendering
     * --------------------------------------------------------
     */

    /**
     * Renders the metric selector switch component.
     * @returns {Element}
     */
    const metricSelector = () => {
        return (
            <Grid container direction="row" justifyContent="flex-start">
                <Grid item>
                    <Typography variant="h5" sx={{fontSize: 16, fontFamily: "Poppins", textAlign: 'left'}} color="white">Metric Selector</Typography>
                    <Paper elevation={1} sx={{
                        borderWidth: 1,
                        borderStyle: 'solid',
                        borderColor: '#FFC540',
                        display: 'flex',
                        background: 'transparent',
                        flexWrap: 'wrap',
                        padding: 0.5,
                    }}>
                        <ToggleButtonGroup
                            color="primary"
                            value={metric}
                            exclusive
                            onChange={e => setMetric(e.target.value)}
                            aria-label="Metric Selector"
                        >
                            {['Size', 'Files'].map((val) => (
                                <ToggleButton
                                    key={val}
                                    value={val}
                                    sx={{
                                        lineHeight: 1.1,
                                        minWidth: 'auto !important',
                                        minHeight: 'auto !important',
                                        textTransform: 'none',
                                        display: 'inline-flex !important',
                                        fontSize: '0.75rem',
                                    }}
                                >
                                    {val}
                                </ToggleButton>
                            ))}
                        </ToggleButtonGroup>
                    </Paper>
                </Grid>
            </Grid>
        );
    };

    return (
        <Box
            sx={{
                width: '100%',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'visible',
            }}
            id="Box 1"
        >
            {!variantMetric &&
                <Box sx={{width: '100%'}}>
                    {metricSelector()}
                </Box>}
            {/* A ring-shaped placeholder rather than a spinner in an empty box. Both halves of Backup Data
                and Storage Policies render through here via DoubleDonutGraph, so this one branch covers
                all four donuts on the dashboard. See graphSkeleton.jsx. */}
            {loading ?
                <DonutChartSkeleton/> :
                <>
                    <Box
                        sx={{
                            position: "relative",
                            flexGrow: 1,
                            display: 'flex',
                            width: '100%',
                            height: '100%',
                            marginLeft: 0,
                            paddingLeft: 0,
                            paddingTop: 1,
                            overflow: 'visible',
                        }}
                        id="Box 2"
                    >
                        {enrichedProcessedData && enrichedProcessedData.length > 1 ? (
                                <>
                                    <Chart
                                        key={variant}
                                        chartType="PieChart"
                                        width="100%"
                                        height="100%"
                                        data={enrichedProcessedData}
                                        options={graphOptions}
                                        // className="InterfaceChart" // class used for custom CSS styling
                                    />
                                </>) :
                            (<Box sx={{justifyContent: 'center', alignItems: 'center', display: 'flex', width: '100%', height: '100%'}}>
                                <Typography variant="h5" sx={{fontSize: 16}} color="white">No data</Typography>
                            </Box>)}
                        {processedDataTotal ? < DonutCenter total={processedDataTotal} metric={metric}/> : <></>}
                    </Box>
                    {footNoteInstructions ?
                        <Box sx={{width: '100%'}}>
                            <Typography variant="h5" sx={{fontSize: 12, fontStyle: 'italic'}} color="white">* Zoom in - click | Zoom out - right click</Typography>
                        </Box> :
                        <></>}
                </>
            }
        </Box>
    );
};